import { Form, useForm } from "@portal/BaseComponents/Form/AntForm";
import { StyledFormItem } from "@portal/BaseComponents/Form/style";
import { FormRules } from "@portal/BaseComponents/Form/type";
import { InputNumber } from "@portal/BaseComponents/Input/AntInput";
import { OnInputNumberChange } from "@portal/BaseComponents/Input/type";
import { Radio, RadioGroup } from "@portal/BaseComponents/Radio/AntRadio";
import { GenericRadioChangeEvent } from "@portal/BaseComponents/Radio/types";
import { Space } from "@portal/BaseComponents/Space/AntSpace";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { CustomFieldOperatorType } from "@portal/types/entities/custom-field";
import {
  NUMBER_ONLY_REGEX,
  NUMBER_WITH_DECIMAL_REGEX,
} from "@portal/util/regex";
import { RuleObject } from "antd/es/form";
import { CustomFieldRangeValueType } from "./NumericCustomField";

const fieldIds = {
  single_values: "single_values",
};

const t = {
  greaterThan: "Greater than or equals",
  lessThan: "Less than or equals",
  from: "From",
  to: "To",
  unassigned: "Is unassigned",
  equalTo: "Equals",
  Between: "Between",
};

interface NumericSingleValuesProps {
  value?: Partial<CustomFieldRangeValueType<CustomFieldOperatorType>>;
  allowDecimal: boolean;
  onErrorCallBack: (value: boolean) => void;
  onChange: (
    value: Partial<CustomFieldRangeValueType<CustomFieldOperatorType>>
  ) => void;
}
function NumericSingleValues(props: NumericSingleValuesProps) {
  const {
    value: defaultValue,
    allowDecimal,
    onErrorCallBack,
    onChange,
  } = props;
  const [form] = useForm();
  const regex = allowDecimal ? NUMBER_WITH_DECIMAL_REGEX : NUMBER_ONLY_REGEX;
  const errorText = allowDecimal
    ? "Value should be a number"
    : "Decimal values are not allowed";

  function handleOptionsChange(
    event: GenericRadioChangeEvent<CustomFieldOperatorType>
  ) {
    form.resetFields();
    onErrorCallBack(false);
    switch (event.target.value) {
      case "GOE":
        onChange({
          value: "1",
          type: "GOE",
          include_unassigned: false,
        });

        break;
      case "LOE":
        onChange({
          value: "1",
          type: "LOE",
          include_unassigned: false,
        });

        break;
      case "EQ":
        onChange({
          value: ["1"],
          type: "IN",
          include_unassigned: false,
        });

        break;

      default:
        break;
    }
  }
  function singleValueValidator(_: RuleObject, inputValue: number) {
    if (!inputValue && inputValue !== 0) {
      onErrorCallBack(true);
      return Promise.reject("Value should not be empty");
    }
    if (!regex.test(inputValue as unknown as string)) {
      onErrorCallBack(true);
      return Promise.reject(errorText);
    }
    onErrorCallBack(false);
    return Promise.resolve();
  }

  function handleSingleValuesChange(...args: OnInputNumberChange) {
    const [value] = args;
    onChange({
      ...defaultValue,
      value:
        defaultValue?.type === "IN" && !defaultValue?.include_unassigned
          ? [String(value)]
          : String(value),
    });
  }
  const singleValueRules: FormRules = [
    {
      validator: singleValueValidator,
    },
  ];
  function getValues(): CustomFieldOperatorType | undefined {
    return defaultValue?.type === "IN" && !defaultValue?.include_unassigned
      ? "EQ"
      : defaultValue?.type;
  }
  const selectedValues = getValues();

  const SingleValues = (
    <Form form={form}>
      <StyledFormItem
        fieldId={fieldIds.single_values}
        shouldUpdate
        name="radio-group"
        className="single-value"
        preserve={false}
        initialValue={Number(defaultValue?.value)}
        rules={singleValueRules}
      >
        <InputNumber controls={false} onChange={handleSingleValuesChange} />
      </StyledFormItem>
    </Form>
  );

  const GreaterThan = (
    <>
      <Radio<CustomFieldOperatorType>
        value="GOE"
        checked={selectedValues === "GOE"}
      >
        <TypographyText>{t.greaterThan}</TypographyText>
      </Radio>
      {selectedValues === "GOE" && SingleValues}
    </>
  );
  const LessThan = (
    <>
      <Radio<CustomFieldOperatorType>
        value="LOE"
        checked={selectedValues === "LOE"}
      >
        <TypographyText>{t.lessThan}</TypographyText>
      </Radio>
      {selectedValues === "LOE" && SingleValues}
    </>
  );
  const Equals = (
    <>
      <Radio<CustomFieldOperatorType>
        value="EQ"
        checked={selectedValues === "EQ"}
      >
        <TypographyText>{t.equalTo}</TypographyText>
      </Radio>
      {selectedValues === "EQ" && SingleValues}
    </>
  );

  return (
    <RadioGroup value={selectedValues} onChange={handleOptionsChange}>
      <Space direction="vertical">
        {GreaterThan}
        {LessThan}
        {Equals}
      </Space>
    </RadioGroup>
  );
}

export { NumericSingleValues };
